import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User, Building2, Menu, X, LogOut, UserCircle, Loader2, LayoutDashboard, RefreshCw, Bookmark, Moon, Sun } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "next-themes";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
const truncateName = (name: string, maxLength: number = 15) => {
  if (name.length > maxLength) {
    return name.substring(0, maxLength) + "...";
  }
  return name;
};
type UserContext = 'candidate' | 'company' | null;
const getUserContext = (): UserContext => {
  const context = localStorage.getItem('userContext');
  if (context === 'candidate' || context === 'company') {
    return context;
  }
  return null;
};
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [userType, setUserType] = useState<'candidate' | 'company' | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [hasMultipleProfiles, setHasMultipleProfiles] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);


  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };
  useEffect(() => {
    setAuthLoading(true);

    // Validate session with server using getUser() instead of getSession()
    const checkUser = async () => {
      const {
        data: {
          user
        },
        error
      } = await supabase.auth.getUser();
      if (error || !user) {
        // Session invalid or expired - clear state
        setUserId(null);
        setUserName(null);
        setUserType(null);
        setAuthLoading(false);
        return;
      }
      setUserId(user.id);
      fetchUserProfile(user.id, user.email);
    };
    checkUser();

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event);

      // Handle sign out or token refresh failure
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
        setUserId(null);
        setUserName(null);
        setUserType(null);
        setAuthLoading(false);
        return;
      }
      if (session?.user) {
        setUserId(session.user.id);
        // Use setTimeout to avoid deadlock
        setTimeout(() => {
          fetchUserProfile(session.user.id, session.user.email);
        }, 0);
      } else {
        setUserId(null);
        setUserName(null);
        setUserType(null);
        setAuthLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const fetchUserProfile = async (userId: string, userEmail?: string | null) => {
    try {
      const userContext = getUserContext();

      // Check both profile types to determine if user has multiple profiles
      const {
        data: candidateData
      } = await supabase.from('candidate_profiles').select('full_name').eq('user_id', userId).maybeSingle();
      const {
        data: companyData
      } = await supabase.from('company_profiles').select('company_name').eq('user_id', userId).maybeSingle();
      const hasCandidate = !!candidateData;
      const hasCompany = !!companyData;
      setHasMultipleProfiles(hasCandidate && hasCompany);

      // Determine which profile to show based on context
      if (userContext === 'company' && hasCompany) {
        setUserName(companyData.company_name);
        setUserType('company');
      } else if (userContext === 'candidate' && hasCandidate) {
        setUserName(candidateData.full_name);
        setUserType('candidate');
      } else if (userContext === null) {
        // No context defined yet - don't show any user
        // Wait for LoginPage to set context or signOut
        setUserName(null);
        setUserType(null);
      } else if (hasCandidate) {
        // Fallback only if context was defined but profile not found
        setUserName(candidateData.full_name);
        setUserType('candidate');
      } else if (hasCompany) {
        setUserName(companyData.company_name);
        setUserType('company');
      }
      setAuthLoading(false);
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setAuthLoading(false);
    }
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUserName(null);
    setUserType(null);
    setUserId(null);
    setHasMultipleProfiles(false);
    localStorage.removeItem('userContext');
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado com sucesso."
    });
    navigate("/");
  };
  const handleSwitchProfile = () => {
    const newContext = userType === 'candidate' ? 'company' : 'candidate';
    localStorage.setItem('userContext', newContext);

    // Navigate to the correct page for the new context
    if (newContext === 'candidate') {
      window.location.href = '/';
    } else {
      window.location.href = '/empresa/dashboard';
    }
  };
  const renderAuthSection = () => {
    if (authLoading) {
      return <Button variant="ghost" size="sm" disabled>
          <Loader2 className="h-4 w-4 animate-spin" />
        </Button>;
    }
    if (userName && userType) {
      return <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" title={userName}>
              {userType === 'candidate' ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
              {truncateName(userName)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {userType === 'candidate' && <>
                <DropdownMenuItem asChild>
                  <Link to="/candidato/minha-conta">
                    <UserCircle className="h-4 w-4 mr-2" />
                    Minha Conta
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/candidato/vagas-salvas">
                    <Bookmark className="h-4 w-4 mr-2" />
                    Vagas Salvas
                  </Link>
                </DropdownMenuItem>
              </>}
            {userType === 'company' && <>
                <DropdownMenuItem asChild>
                  <Link to="/empresa/dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/empresa/minha-conta">
                    <UserCircle className="h-4 w-4 mr-2" />
                    Minha Conta
                  </Link>
                </DropdownMenuItem>
              </>}
            {hasMultipleProfiles && <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSwitchProfile}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {userType === 'candidate' ? 'Trocar para Empresa' : 'Trocar para Candidato'}
                </DropdownMenuItem>
              </>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>;
    }
    return <Button variant="default" size="sm" asChild>
        <Link to="/entrar" className="gap-2">
          Entrar
        </Link>
      </Button>;
  };
  const renderMobileAuthSection = () => {
    if (authLoading) {
      return <div className="flex items-center justify-center px-4 py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>;
    }
    if (userName && userType) {
      return <>
          <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground" title={userName}>
            {userType === 'candidate' ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
            {truncateName(userName)}
          </div>
          {userType === 'candidate' && <>
              <Link to="/candidato/minha-conta" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <UserCircle className="h-4 w-4" />
                Minha Conta
              </Link>
              <Link to="/candidato/vagas-salvas" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Bookmark className="h-4 w-4" />
                Vagas Salvas
              </Link>
            </>}
          {userType === 'company' && <>
              <Link to="/empresa/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
              <Link to="/empresa/minha-conta" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <UserCircle className="h-4 w-4" />
                Minha Conta
              </Link>
            </>}
          {hasMultipleProfiles && <button onClick={() => {
          handleSwitchProfile();
          setMobileMenuOpen(false);
        }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:text-primary hover:bg-muted rounded-lg transition-colors w-full">
              <RefreshCw className="h-4 w-4" />
              {userType === 'candidate' ? 'Trocar para Empresa' : 'Trocar para Candidato'}
            </button>}
          <button onClick={() => {
          handleLogout();
          setMobileMenuOpen(false);
        }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors w-full">
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </>;
    }
    return <Link to="/entrar" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
        Entrar
      </Link>;
  };
  return <header className="sticky top-0 z-50 glass border-b">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo - Esquerda */}
        <div className="flex items-center">
          <Link to={userType === 'company' ? '/empresa/dashboard' : '/'} className="flex items-center gap-3">
            <span className="text-2xl font-display font-bold"><span className="text-accent">JobWay</span><span className="text-primary">X</span></span>
          </Link>
        </div>
        
        {/* Navegação - Centro */}
        <nav className="hidden md:flex items-center gap-6">
          {userType !== 'company' && <>
              <Link to="/vagas" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Vagas
              </Link>
              <Link to="/empresas" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Empresas
              </Link>
            </>}
          {userType === 'company' && <Link to="/empresa/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Dashboard
            </Link>}
          <Link to="/sobre" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Sobre
          </Link>
          <Link to="/contato" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Contato
          </Link>
        </nav>

        {/* Toggle tema + Auth - Direita */}
        <div className="hidden md:flex items-center gap-3">
          {mounted && (
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Alternar tema">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          )}
          {renderAuthSection()}
        </div>

        <div className="md:hidden flex items-center gap-2">
          {mounted && (
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Alternar tema">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          )}
          <button className="p-2 hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          {/* Overlay para fechar menu ao clicar fora */}
          <div 
            className="fixed top-16 left-0 right-0 bottom-0 bg-black/20 md:hidden z-40"
            onClick={() => setMobileMenuOpen(false)}
            onTouchStart={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="md:hidden border-t bg-card animate-fade-in relative z-50">
            <nav className="container py-4 flex flex-col gap-2">
              {userType !== 'company' && <>
                  <Link to="/vagas" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Vagas
                  </Link>
                  <Link to="/empresas" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Empresas
                  </Link>
                </>}
              {userType === 'company' && <Link to="/empresa/dashboard" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Dashboard
                </Link>}
              <Link to="/sobre" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Sobre
              </Link>
              <Link to="/contato" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Contato
              </Link>
              <div className="border-t my-2" />
              {renderMobileAuthSection()}
            </nav>
          </div>
        </>
      )}
    </header>;
}